import FormatCurrency from 'components/Common/FormatCurrency'
import OfferInclusionItem from 'components/Common/Inclusions/OfferInclusionItem'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import LabeledSheet from 'LuxKit/LabeledSheet'
import TextLink from 'LuxKit/TextLink'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { INCLUSIONS_TITLE_LUXPLUS } from 'constants/inclusions'
import useListTruncator from 'hooks/useListTruncator'
import { isLEOffer } from 'lib/offer/offerTypes'
import { LUXURY_PLUS } from 'luxPlus/constants/base'
import { getIsLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import { rem } from 'polished'
import type { ComponentProps, MouseEventHandler } from 'react'
import React, { useCallback } from 'react'
import { connect } from 'react-redux'
import styled from 'styled-components'
import LuxPlusLabelWithModal from './LuxPlusLabelWithModal'
import { LUX_PLUS_MONTH_TAKEOVER_ID } from 'takeovers/luxPlusMonthTakeover/constants/luxPlusMonthTakeoverConstants'
import { useCurrentSiteTakeover } from 'takeovers/utils/hooks/useCurrentSiteTakeover'
import LuxPlusMonthLabel from 'takeovers/luxPlusMonthTakeover/components/LuxPlusMonthLabel'

const List = styled(VerticalSpacer)`
  padding: ${rem(8)};
`

interface MappedProps {
  subscription: App.ProductSubscription
  isLuxPlusEnabled: boolean
}

type InheritedOfferInclusionItemProps = Pick<ComponentProps<typeof OfferInclusionItem>, 'showMinStayDescription'>

interface Props extends InheritedOfferInclusionItemProps {
  luxPlusInclusions: Array<App.OfferInclusion>
  accommodationOffer?: App.Offer | App.OfferSummary | App.CruiseOffer
  hideUpsellModal?: boolean
  isModalPrimaryActionHidden?: boolean
  /**
   * The number of items to display before the rest are truncated.
   *
   * If set to zero, nothing will be displayed.
   */
  truncateAfter?: number
  onShowMoreClick?: () => void
}

function LuxPlusInclusions({
  luxPlusInclusions,
  showMinStayDescription,
  isLuxPlusEnabled,
  accommodationOffer,
  subscription,
  hideUpsellModal,
  isModalPrimaryActionHidden,
  truncateAfter,
  onShowMoreClick,
}: Props & MappedProps) {
  const [alwaysVisibleInclusions, collapsibleInclusions] = useListTruncator(truncateAfter, luxPlusInclusions)
  const handleMoreClick = useCallback<MouseEventHandler<HTMLAnchorElement>>((event) => {
    event.preventDefault()
    onShowMoreClick?.()
  }, [onShowMoreClick])

  const isLPMonthTakeoverEnabled = useCurrentSiteTakeover({ offer: accommodationOffer })?.id === LUX_PLUS_MONTH_TAKEOVER_ID

  if (!isLuxPlusEnabled || !alwaysVisibleInclusions.length) return null

  return <LabeledSheet
    data-testid="lux-plus-bonus-inclusions"
    kind="lux-plus"
    label={
      <>
        <CSSBreakpoint not="tablet">
          {!isLPMonthTakeoverEnabled && <LuxPlusLabelWithModal
            type="bonus-inclusions"
            inclusions={luxPlusInclusions}
            offer={accommodationOffer}
            hideUpsellModal={hideUpsellModal}
            isModalPrimaryActionHidden={isModalPrimaryActionHidden}
          />}
          {isLPMonthTakeoverEnabled && <LuxPlusMonthLabel
            type="bonus-inclusions"
          />}
        </CSSBreakpoint>
        <CSSBreakpoint only="tablet">
          <LuxPlusLabelWithModal
            type="only-logo"
            offer={accommodationOffer}
            inclusions={luxPlusInclusions}
            hideUpsellModal={hideUpsellModal}
            isModalPrimaryActionHidden={isModalPrimaryActionHidden}
            modalTitle={`Join ${LUXURY_PLUS.PROGRAM_NAME} to enjoy ${INCLUSIONS_TITLE_LUXPLUS} for this offer and more!`}
            modalSubTitle={<>
              Become a member of our VIP travel club for <FormatCurrency value={subscription.offers?.[0].price ?? 0} format="rounded" />{' '}
              per year to unlock {INCLUSIONS_TITLE_LUXPLUS} when you book{' '}
              {isLEOffer(accommodationOffer) ? <b>{accommodationOffer?.property?.name}</b> : 'this hotel'}.
            </>}
          />
        </CSSBreakpoint>
      </>
    }
  >
    <List gap={4} data-testid="luxury-plus-inclusions">
      {alwaysVisibleInclusions.map((inclusion) => <OfferInclusionItem
        key={inclusion.id}
        inclusion={inclusion}
        type="lux-plus"
        showMinStayDescription={showMinStayDescription}
      />)}
      {(onShowMoreClick && !!collapsibleInclusions.length) && <TextLink size="medium" onClick={handleMoreClick}>+{collapsibleInclusions.length} more</TextLink>}
    </List>
  </LabeledSheet>
}

export default connect<MappedProps, {}, Props, App.State>((state: App.State) => {
  return {
    isLuxPlusEnabled: getIsLuxPlusEnabled(state),
    subscription: state.luxPlus.products.subscription,
  }
})(LuxPlusInclusions)
